import React, { useEffect, useState } from 'react';
import { Box, Container, Drawer, Stack } from '@mui/material';
import DuoButton from '../DuoButton';
import LanguageMenu from '../LanguageMenu';
import { VaqtlyMark, VAQTLY_INK } from '../ProductLogos';
import { PALETTE, RADIUS } from '../../theme';
import { useT } from '../../lib/i18n';

/** The anchor targets the nav points at, in page order. */
const SECTIONS = ['products', 'team', 'about'] as const;
type SectionId = (typeof SECTIONS)[number];

/**
 * Which section the reader is currently looking at.
 *
 * The observer's root is squeezed to a band across the middle of the viewport,
 * so "active" means "crossing the centre of the screen" rather than "touching
 * the edge" — with full-height sections, an edge test flips the highlight while
 * the previous section still fills the view.
 */
function useActiveSection(): SectionId | null {
  const [active, setActive] = useState<SectionId | null>(null);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const id = entry.target.id as SectionId;
          if (entry.isIntersecting) setActive(id);
          else setActive((current) => (current === id ? null : current));
        });
      },
      { rootMargin: '-45% 0px -50% 0px' },
    );

    SECTIONS.forEach((id) => {
      const node = document.getElementById(id);
      if (node) observer.observe(node);
    });

    return () => observer.disconnect();
  }, []);

  return active;
}

/**
 * Sticky header.
 *
 * Laid out as a three-column grid — logo, navigation, actions — rather than with
 * an auto margin. A grid states the three zones outright, keeps the navigation
 * optically centred whatever the side groups weigh, and cannot be undone by a
 * parent's spacing rule the way `margin-right: auto` on a Stack child is: MUI's
 * `spacing` emits `margin: 0` on every child before applying its own gap, which
 * silently wiped the auto margin and collapsed the whole bar to the left.
 *
 * Below `md` the navigation moves into a drawer, so the bar keeps the wordmark,
 * the language switch and one control instead of crushing five things together.
 */
export default function SiteHeader() {
  const t = useT();
  const active = useActiveSection();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const nav: Array<{ id: SectionId; label: string }> = [
    { id: 'products', label: t.nav.products },
    { id: 'team', label: t.nav.team },
    { id: 'about', label: t.nav.about },
  ];

  return (
    <Box
      component="header"
      sx={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        backgroundColor: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(10px)',
        borderBottom: `2px solid ${scrolled ? PALETTE.hairline : 'transparent'}`,
        transition: 'border-color 200ms ease',
      }}
    >
      <Container maxWidth="lg">
        <Box
          sx={{
            display: 'grid',
            /* The centre column disappears with the nav below md, leaving the two
               side zones to fill the bar. */
            gridTemplateColumns: { xs: '1fr auto', md: '1fr auto 1fr' },
            alignItems: 'center',
            height: { xs: 68, md: 80 },
          }}
        >
          <Box
            component="a"
            href="#top"
            aria-label="Vaqtly Holding"
            sx={{
              justifySelf: 'start',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1.25,
              textDecoration: 'none',
            }}
          >
            <VaqtlyMark size={36} />
            {/* The wordmark's own near-black from the lockup, not the body ink. */}
            <Box component="span" sx={{ fontSize: 23, fontWeight: 800, color: VAQTLY_INK, letterSpacing: '-0.02em' }}>
              vaqtly
            </Box>
          </Box>

          <Box component="nav" sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5, justifySelf: 'center' }}>
            {nav.map((item) => (
              <NavLink key={item.id} href={`#${item.id}`} active={active === item.id}>
                {item.label}
              </NavLink>
            ))}
          </Box>

          <Stack direction="row" alignItems="center" sx={{ justifySelf: 'end', gap: 1 }}>
            <LanguageMenu />

            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <DuoButton duoSize="sm" component="a" href="#contact">
                {t.nav.contact}
              </DuoButton>
            </Box>

            <MenuToggle open={menuOpen} onClick={() => setMenuOpen(true)} />
          </Stack>
        </Box>
      </Container>

      <Drawer
        anchor="right"
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        slotProps={{ paper: { sx: { width: 280, p: 2.5, borderLeft: `2px solid ${PALETTE.hairline}` } } }}
      >
        <Stack direction="row" justifyContent="flex-end">
          <MenuToggle open onClick={() => setMenuOpen(false)} />
        </Stack>

        <Stack component="nav" spacing={0.5} sx={{ mt: 2 }}>
          {nav.map((item) => (
            <NavLink
              key={item.id}
              href={`#${item.id}`}
              active={active === item.id}
              onClick={() => setMenuOpen(false)}
              block
            >
              {item.label}
            </NavLink>
          ))}
        </Stack>

        <Box sx={{ mt: 3 }}>
          <DuoButton block component="a" href="#contact" onClick={() => setMenuOpen(false)}>
            {t.nav.contact}
          </DuoButton>
        </Box>
      </Drawer>
    </Box>
  );
}

/** A nav item. The active one is marked by weight and a soft fill, not colour alone. */
function NavLink({
  href,
  active,
  block = false,
  onClick,
  children,
}: {
  href: string;
  active: boolean;
  block?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  return (
    <Box
      component="a"
      href={href}
      onClick={onClick}
      aria-current={active ? 'true' : undefined}
      sx={{
        display: block ? 'block' : 'inline-block',
        px: 2,
        py: block ? 1.5 : 1,
        borderRadius: '12px',
        fontSize: block ? 17 : 15,
        fontWeight: 800,
        textDecoration: 'none',
        color: active ? PALETTE.signal : PALETTE.inkMuted,
        backgroundColor: active ? PALETTE.signalSoft : 'transparent',
        transition: 'color 150ms ease, background-color 150ms ease',
        '&:hover': {
          color: active ? PALETTE.signal : PALETTE.ink,
          backgroundColor: active ? PALETTE.signalSoft : PALETTE.groundSunken,
        },
      }}
    >
      {children}
    </Box>
  );
}

/** Hamburger / close, drawn rather than pulled from an icon set. */
function MenuToggle({ open, onClick }: { open: boolean; onClick: () => void }) {
  return (
    <Box
      component="button"
      type="button"
      onClick={onClick}
      aria-label="Menu"
      aria-expanded={open}
      sx={{
        display: { xs: 'inline-flex', md: 'none' },
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 40,
        p: 0,
        border: `2px solid ${PALETTE.hairline}`,
        borderRadius: `${RADIUS.control}px`,
        backgroundColor: PALETTE.ground,
        color: PALETTE.ink,
        cursor: 'pointer',
        transition: 'border-color 150ms ease',
        '&:hover': { borderColor: PALETTE.inkFaint },
      }}
    >
      <Box component="svg" viewBox="0 0 20 20" aria-hidden="true" sx={{ width: 18, height: 18, fill: 'currentColor' }}>
        {open ? (
          <path d="M4.5 3 10 8.5 15.5 3 17 4.5 11.5 10 17 15.5 15.5 17 10 11.5 4.5 17 3 15.5 8.5 10 3 4.5z" />
        ) : (
          <>
            <rect x="2" y="4" width="16" height="2.6" rx="1.3" />
            <rect x="2" y="8.7" width="16" height="2.6" rx="1.3" />
            <rect x="2" y="13.4" width="16" height="2.6" rx="1.3" />
          </>
        )}
      </Box>
    </Box>
  );
}
